import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Smartphone, Star, Download, Users, CheckCircle, Target, Zap, Apple, PlayCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const MobilePortfolio = () => {
  const project = {
    title: 'FitLife - Sağlık & Fitness Uygulaması',
    client: 'FitLife Inc.',
    duration: '20 Hafta',
    category: 'Mobil Uygulama',
    year: '2025',
    heroImage: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&h=600&fit=crop',
    description: 'FitLife için iOS ve Android platformlarında çalışan kapsamlı bir fitness uygulaması geliştirdik. Yapay zeka destekli antrenman programları, beslenme takibi ve sosyal özellikler ile kullanıcı deneyimini en üst seviyeye çıkardık.',
    challenge: 'FitLife, kullanıcılarına kişiselleştirilmiş fitness deneyimi sunmak istiyordu. Mevcut çözümler ya çok karmaşık ya da yetersiz özellikler sunuyordu.',
    solution: 'React Native ile cross-platform bir uygulama geliştirdik. AI destekli kişiselleştirme, gamification ve sosyal özellikler ekleyerek kullanıcı bağlılığını artırdık.'
  };

  const stats = [
    { label: 'App Store Puanı', value: '4.9', icon: Star },
    { label: 'İndirme', value: '500K+', icon: Download },
    { label: 'Aktif Kullanıcı', value: '120K', icon: Users },
    { label: 'Retention Rate', value: '68%', icon: Target }
  ];

  const appFeatures = [
    { title: 'AI Antrenman Planları', desc: 'Kullanıcı hedeflerine göre otomatik program oluşturma' },
    { title: 'Kalori & Makro Takibi', desc: 'Barkod okuyucu ile kolay besin girişi' },
    { title: 'Canlı Antrenmanlar', desc: 'Profesyonel eğitmenlerle canlı dersler' },
    { title: 'İlerleme Takibi', desc: 'Detaylı istatistikler ve görsel grafikler' },
    { title: 'Sosyal Özellikler', desc: 'Arkadaşlarla yarışma ve motivasyon' },
    { title: 'Wearable Entegrasyonu', desc: 'Apple Watch ve Fitbit desteği' }
  ];

  const techStack = ['React Native', 'TypeScript', 'Redux Toolkit', 'Node.js', 'MongoDB', 'Firebase', 'TensorFlow Lite', 'HealthKit/Google Fit'];

  const screenshots = [
    'https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400&h=800&fit=crop',
    'https://images.unsplash.com/photo-1526256262350-7da7584cf5eb?w=400&h=800&fit=crop',
    'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=400&h=800&fit=crop'
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/?scrollTo=portfolio"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Portfolyoya Dön
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-sm font-medium rounded-full">
                  {project.category}
                </span>
                <span className="text-gray-500">{project.year}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {project.title}
              </h1>

              <p className="text-xl text-gray-400 mb-8">{project.description}</p>

              <div className="flex flex-wrap gap-6 text-sm mb-8">
                <div>
                  <span className="text-gray-500">Müşteri</span>
                  <p className="text-white font-medium">{project.client}</p>
                </div>
                <div>
                  <span className="text-gray-500">Süre</span>
                  <p className="text-white font-medium">{project.duration}</p>
                </div>
              </div>

              {/* App Store Buttons */}
              <div className="flex gap-4">
                <button className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl px-4 py-3 transition-all">
                  <Apple className="w-6 h-6 text-white" />
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Download on the</div>
                    <div className="text-white font-medium">App Store</div>
                  </div>
                </button>
                <button className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl px-4 py-3 transition-all">
                  <PlayCircle className="w-6 h-6 text-white" />
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Get it on</div>
                    <div className="text-white font-medium">Google Play</div>
                  </div>
                </button>
              </div>
            </div>

            <div className="relative">
              <img
                src={project.heroImage}
                alt={project.title}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 shadow-xl">
                <Smartphone className="w-8 h-8 text-white mb-2" />
                <div className="text-white font-bold text-2xl">500K+</div>
                <div className="text-purple-100 text-sm">Downloads</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 text-center hover:border-purple-500/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Uygulama <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Ekranları</span>
          </h2>

          <div className="flex justify-center gap-6 overflow-x-auto pb-4">
            {screenshots.map((screen, index) => (
              <div key={index} className="flex-shrink-0 relative">
                <div className="w-64 h-[520px] bg-slate-800 rounded-[3rem] p-3 border-4 border-slate-700 shadow-2xl">
                  <div className="w-full h-full rounded-[2.5rem] overflow-hidden">
                    <img
                      src={screen}
                      alt={`Screenshot ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Notch */}
                  <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-slate-800 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Zorluk</h3>
              <p className="text-gray-400">{project.challenge}</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Çözüm</h3>
              <p className="text-gray-400">{project.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Uygulama <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Özellikleri</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {appFeatures.map((feature, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 hover:border-purple-500/50 transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/30 transition-colors">
                    <CheckCircle className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{feature.title}</h3>
                    <p className="text-gray-400 text-sm">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Kullanılan <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Teknolojiler</span>
          </h2>

          <div className="flex flex-wrap justify-center gap-4">
            {techStack.map((tech, index) => (
              <span 
                key={index}
                className="px-5 py-2 bg-slate-800/50 border border-slate-700/50 rounded-full text-gray-300 hover:border-purple-500/50 hover:text-purple-400 transition-all"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Mobil Uygulamanızı
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Birlikte Geliştirelim</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Fikrinizi App Store ve Google Play'de milyonlara ulaştıralım.
          </p>
          <Button 
            onClick={() => window.location.href = '/iletisim'}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-lg rounded-xl"
          >
            Ücretsiz Görüşme Talep Et
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MobilePortfolio;
